import React, { useEffect, useState, useCallback } from 'react';
import { _registerConfirmSubscriber } from '../utils/notify';
import './ConfirmHost.css';

/**
 * Singleton host for the promise-based ``confirmAsync`` modal.
 *
 * Mounted once at the app root (alongside ``<Toaster>``). Listens for
 * confirm requests dispatched by ``confirmAsync`` and renders a single
 * modal with cancel / confirm buttons. The promise registered by the
 * caller is resolved when the user clicks one of the two buttons or
 * dismisses the modal (Escape / backdrop click → cancel).
 *
 * Keyboard:
 *   - Enter  → confirm
 *   - Escape → cancel
 */
function ConfirmHost() {
  const [request, setRequest] = useState(null);

  useEffect(() => {
    const unsubscribe = _registerConfirmSubscriber((req) => setRequest(req));
    return unsubscribe;
  }, []);

  const close = useCallback(
    (result) => {
      if (!request) return;
      request.resolve(result);
      setRequest(null);
    },
    [request]
  );

  useEffect(() => {
    if (!request) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close(false);
      else if (e.key === 'Enter') close(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request, close]);

  if (!request) return null;

  return (
    <div
      className="confirm-host-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={request.title ? `confirm-title-${request.id}` : undefined}
      aria-describedby={`confirm-msg-${request.id}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) close(false);
      }}
    >
      <div className="confirm-host-modal">
        {request.title && (
          <h3 id={`confirm-title-${request.id}`} className="confirm-host-title">
            {request.title}
          </h3>
        )}
        <p id={`confirm-msg-${request.id}`} className="confirm-host-message">
          {request.message}
        </p>
        <div className="confirm-host-actions">
          <button
            type="button"
            className="confirm-host-btn confirm-host-btn-cancel"
            onClick={() => close(false)}
            autoFocus
          >
            {request.cancelLabel}
          </button>
          <button
            type="button"
            className={
              request.danger
                ? 'confirm-host-btn confirm-host-btn-danger'
                : 'confirm-host-btn confirm-host-btn-primary'
            }
            onClick={() => close(true)}
          >
            {request.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmHost;
